import { useTranslation } from "react-i18next";

const ONE_MILLION = 1_000_000;
// map ภาษาแอป → locale ของ Intl (en=K/M/B · จีน zh=万/亿) · ไทยจัดการเองด้านล่าง
const INTL_LOCALE = { en: "en", zh: "zh", ch: "zh", cn: "zh" };

// - ไทย: ย่อเฉพาะ "ล้าน" (< 1,000,000 โชว์เลขเต็ม · ≥ ล้าน → "x.x ล้าน")
// - อังกฤษ: K/M/B (Intl) · จีน: 万/亿 (Intl)
const formatCompact = (n, lang) => {
  const key = String(lang || "en")
    .split("-")[0]
    .toLowerCase();
  const num = Number(n) || 0;

  if (key === "th") {
    if (Math.abs(num) >= ONE_MILLION) {
      const m = (num / ONE_MILLION).toLocaleString("en-US", {
        maximumFractionDigits: 1,
      });
      return `${m} ล้าน`;
    }
    return Math.round(num).toLocaleString("en-US");
  }

  return new Intl.NumberFormat(INTL_LOCALE[key] || "en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(num);
};

const buildDonutGradient = (segments) => {
  const total = segments.reduce((sum, seg) => sum + seg.pct, 0) || 1;
  let cumulative = 0;
  const stops = segments.map((seg) => {
    const start = (cumulative / total) * 100;
    cumulative += seg.pct;
    const end = (cumulative / total) * 100;
    return `${seg.color} ${start}% ${end}%`;
  });
  return `conic-gradient(${stops.join(", ")})`;
};

const ShipmentStatusDonut = ({ segments, total }) => {
  // `seg.label` (Delivered / In Transit ...) มาจาก BE — ยังเป็นอังกฤษตาม §7.8
  const { t, i18n } = useTranslation("dashboard");
  const gradient = buildDonutGradient(segments);

  return (
    <div className="es-chart-card">
      <div className="es-chart-card-title">{t("Chart.Shipment_Status")}</div>
      <div className="es-donut-wrap">
        <div className="es-donut" style={{ background: gradient }}>
          <div className="es-donut-center">
            <strong title={`${(Number(total) || 0).toLocaleString()} ${t("Chart.Total")}`}>
              {formatCompact(total, i18n.language)}
            </strong>
            <span>{t("Chart.Total")}</span>
          </div>
        </div>
        <div className="es-legend">
          {segments.map((seg) => (
            <div key={seg.label} className="es-legend-item">
              <span className="es-legend-dot" style={{ background: seg.color }} />
              <span className="es-legend-label">{seg.label}</span>
              <span className="es-legend-value">
                {seg.count} <em>({seg.pct}%)</em>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Distinct hue-zone palette for trend lines — FE display-only override, independent of BE color field.
// Index order: oldest year first (same order BE sends series). Add more colors if years expand.
const TREND_PALETTE = [
  "#16a34a", // green  — year[0]
  "#eab308", // yellow — year[1]
  "#dc2626", // red    — year[2]
  "#7c3aed", // violet     — year[3] (future-proof)
];

const ShipmentTrendChart = ({ months, series }) => {
  // ชื่อเดือนบนแกน X (`months`) มาจาก BE — ยังเป็นอังกฤษตาม §7.8
  const { t } = useTranslation("dashboard");
  // Remap series colors to distinct hue zones (BE color field ignored for display)
  const coloredSeries = series.map((s, idx) => ({
    ...s,
    color: TREND_PALETTE[idx % TREND_PALETTE.length],
  }));
  const width = 520;
  const height = 200;
  const padding = { top: 16, right: 16, bottom: 28, left: 44 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const allValues = coloredSeries.flatMap((s) => s.values);
  const minY = Math.min(...allValues) - 40;
  const maxY = Math.max(...allValues) + 40;

  const xStep = chartW / (months.length - 1);
  const scaleY = (val) => padding.top + chartH - ((val - minY) / (maxY - minY)) * chartH;
  const scaleX = (idx) => padding.left + idx * xStep;
  const gridRatios = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="es-chart-card">
      <div className="es-chart-card-title">{t("Chart.Shipment_Trend")}</div>
      <svg
        className="es-line-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={t("Chart.Shipment_Trend_Alt")}
      >
        {gridRatios.map((ratio) => {
          const y = padding.top + chartH * (1 - ratio);
          const value = Math.round(minY + ratio * (maxY - minY));
          return (
            <g key={ratio}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padding.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
                {value.toLocaleString()}
              </text>
            </g>
          );
        })}
        {coloredSeries.map((s) => {
          const points = s.values.map((val, idx) => `${scaleX(idx)},${scaleY(val)}`).join(" ");
          return (
            <polyline
              key={s.year}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeLinejoin="round"
              points={points}
            />
          );
        })}
        {coloredSeries.map((s) =>
          s.values.map((val, idx) => (
            <circle key={`${s.year}-${idx}`} cx={scaleX(idx)} cy={scaleY(val)} r="2.5" fill={s.color} />
          ))
        )}
        {months.map((month, idx) => (
          <text key={month} x={scaleX(idx)} y={height - 6} textAnchor="middle" fontSize="10" fill="#6b7280">
            {month}
          </text>
        ))}
        <g transform={`translate(${padding.left}, 8)`}>
          {coloredSeries.map((s, idx) => (
            <g key={s.year} transform={`translate(${idx * 72}, 0)`}>
              <line x1="0" y1="4" x2="14" y2="4" stroke={s.color} strokeWidth="2" />
              <text x="18" y="8" fontSize="10" fill="#374151">
                {s.year}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

const CompanyTopList = ({ items }) => {
  const { t } = useTranslation("dashboard");

  return (
    <div className="es-chart-card" style={{ paddingTop: "15px" }}>
      <div className="es-chart-card-title">{t("Chart.Company_Top")}</div>
      <div className="es-top-list">
        {items.map((item) => (
          <div key={item.code} className="es-top-item">
            <span className="es-top-rank">{item.rank}.</span>
            <span className="es-top-code">{item.code}</span>
            <span className="es-top-volume">
              {item.volume} {t("Metric.CBM")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChartsSection = ({ shipmentStatus, shipmentTotal, trend, companies }) => {
  return (
    <div className="es-charts-grid">
      <ShipmentStatusDonut segments={shipmentStatus} total={shipmentTotal} />
      <ShipmentTrendChart months={trend.months} series={trend.series} />
      <CompanyTopList items={companies} />
    </div>
  );
};

export default ChartsSection;
